import { useState, useRef } from "react";
import { Reorder } from "framer-motion";
import { GripVertical, Trash2, Plus, Save, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getPhotoUrl(photo) {
  if (photo?.thumbnails?.large?.url) return photo.thumbnails.large.url;
  if (photo?.thumbnails?.full?.url) return photo.thumbnails.full.url;
  return photo?.url || "";
}

export default function PhotoGallery({
  photos = [],
  fieldType,
  title,
  token,
  recordId,
  onUpdate,
}) {
  const [items, setItems] = useState(photos);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reordered, setReordered] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Sync if parent updates photos
  const prevPhotosRef = useRef(photos);
  if (photos !== prevPhotosRef.current) {
    prevPhotosRef.current = photos;
    setItems(photos);
    setReordered(false);
  }

  const authHeader = { Authorization: `Bearer ${token}` };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("record_id", recordId);
      formData.append("field_type", fieldType);
      formData.append("file", file);
      const { data } = await axios.post(`${API}/artist/upload`, formData, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      setItems(data.profile[fieldType === "photos" ? "photos" : "stick_art"]);
      onUpdate(data.profile);
      setReordered(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm("Remove this image?")) return;
    setDeletingId(attachmentId);
    setError("");
    try {
      const { data } = await axios.delete(`${API}/artist/attachment`, {
        headers: { ...authHeader, "Content-Type": "application/json" },
        data: { record_id: recordId, field_type: fieldType, attachment_id: attachmentId },
      });
      setItems(data.profile[fieldType === "photos" ? "photos" : "stick_art"]);
      onUpdate(data.profile);
      setReordered(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleReorder = (newOrder) => {
    setItems(newOrder);
    setReordered(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    setError("");
    try {
      const orderedIds = items.map((i) => i.id).filter(Boolean);
      const { data } = await axios.patch(
        `${API}/artist/reorder`,
        { record_id: recordId, field_type: fieldType, ordered_ids: orderedIds },
        { headers: authHeader }
      );
      setItems(data.profile[fieldType === "photos" ? "photos" : "stick_art"]);
      onUpdate(data.profile);
      setReordered(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Save order failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gallery-section" data-testid={`gallery-${fieldType}`}>
      <div className="gallery-header">
        <h2 className="section-title">{title}</h2>
        <div className="gallery-actions">
          {reordered && (
            <button
              className="btn-save-order"
              onClick={handleSaveOrder}
              disabled={saving}
              data-testid={`save-order-${fieldType}`}
            >
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
              {saving ? "SAVING..." : "SAVE ORDER"}
            </button>
          )}
          <button
            className="btn-add-photo"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`add-photo-${fieldType}`}
          >
            {uploading ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
            {uploading ? "UPLOADING..." : "ADD IMAGE"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            style={{ display: "none" }}
            onChange={handleUpload}
            data-testid={`file-input-${fieldType}`}
          />
        </div>
      </div>

      {error && <div className="gallery-error" data-testid={`gallery-error-${fieldType}`}>{error}</div>}

      {items.length === 0 ? (
        <div className="gallery-empty" data-testid={`gallery-empty-${fieldType}`}>
          <p>No images yet. Click ADD IMAGE to upload.</p>
        </div>
      ) : (
        <Reorder.Group
          axis="x"
          values={items}
          onReorder={handleReorder}
          className="gallery-grid"
          data-testid={`gallery-grid-${fieldType}`}
        >
          {items.map((photo, index) => (
            <Reorder.Item
              key={photo.id || photo.url}
              value={photo}
              className={`gallery-item ${index === 0 && fieldType === "photos" ? "gallery-item-primary" : ""}`}
              data-testid={`gallery-item-${photo.id}`}
            >
              <div className="gallery-img-wrap">
                <img
                  src={getPhotoUrl(photo)}
                  alt={photo.filename || "image"}
                  className="gallery-img"
                  loading="lazy"
                />
                {index === 0 && fieldType === "photos" && (
                  <span className="gallery-badge">PROFILE PHOTO</span>
                )}
                <div className="gallery-overlay">
                  <div className="gallery-drag-handle">
                    <GripVertical size={16} />
                  </div>
                  <button
                    className="gallery-delete-btn"
                    onClick={() => handleDelete(photo.id)}
                    disabled={deletingId === photo.id}
                    data-testid={`delete-photo-${photo.id}`}
                    title="Remove image"
                  >
                    {deletingId === photo.id ? (
                      <Loader2 size={14} className="spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              </div>
              <p className="gallery-filename">{photo.filename || `Image ${index + 1}`}</p>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}
    </div>
  );
}
